import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { CurrencyInput } from 'transfix-ui/components/CurrencyInput'
import { FormModal } from 'transfix-ui/components/Modals'
import { Switch } from 'transfix-ui/components/Switch'
import { IShipment } from 'types/shipment'
import {
  closeMarketplaceSettingsModal,
  updateMarketplaceSettings,
} from 'utils/actions/loadBoardPosts'
import { DAT_FLAGS } from 'apps/booking/constants/loadBoardPosts'
import { IStore } from 'apps/booking/utils/reducers'
import { useMPAnalytics } from 'apps/marketplace/hooks'

export interface IMarketplaceSettingsState {
  rate?: number
  marketOnMarketplace: boolean
  marketOnLoadBoards: boolean
}

interface ISelector {
  shipment?: IShipment
  showSettingsModal: boolean
  savingSettings: boolean
  settingError?: string
}

export const MarketplaceSettingsDialog = React.memo(() => {
  const { trackEvent } = useMPAnalytics()
  const { savingSettings, settingError, shipment, showSettingsModal } = useSelector<
    IStore,
    ISelector
  >(({ loadBoardPosts, sidebar: { data } }) => ({
    shipment: data?.shipment,
    showSettingsModal: loadBoardPosts.showSettingsModal,
    savingSettings: loadBoardPosts.savingSettings,
    settingError: loadBoardPosts.settingError,
  }))

  const [currentSettings, setCurrentSettings] = useState<IMarketplaceSettingsState>({
    rate: 0,
    marketOnMarketplace: false,
    marketOnLoadBoards: false,
  })

  const dispatch = useDispatch()
  const closeMarketplaceSettings = () => {
    dispatch(closeMarketplaceSettingsModal())
  }

  const saveMarketplaceSettings = () => {
    if (shipment) {
      trackEvent('Booking', 'CLICKED_SAVE_MARKETPLACE_SETTINGS', {
        shipmentId: shipment.id,
      })
      dispatch(updateMarketplaceSettings(String(shipment.id), currentSettings))
    }
  }

  useEffect(() => {
    setCurrentSettings({
      rate: Number(shipment?.carrierPayment.tcmCarrierPrices?.listPrice),
      marketOnMarketplace: Boolean(shipment?.flags?.find(f => f === DAT_FLAGS.marketOnMarketplace)),
      marketOnLoadBoards: Boolean(shipment?.flags?.find(f => f === DAT_FLAGS.marketOnLoadBoards)),
    })
  }, [shipment?.flags, shipment?.carrierPayment.tcmCarrierPrices?.listPrice])

  const changeRate = (e: { value: string }) => {
    setCurrentSettings({ ...currentSettings, rate: e.value ? Number(e.value) : undefined })
  }

  const changeFlag = (e: React.FormEvent<HTMLInputElement>) => {
    const datFlag = e.currentTarget.id as keyof typeof DAT_FLAGS
    setCurrentSettings({ ...currentSettings, [datFlag]: !currentSettings[datFlag] })
  }

  return (
    <FormModal
      title='Edit Marketplace Settings'
      size={4}
      open={showSettingsModal}
      onClose={closeMarketplaceSettings}
      error={settingError}
      dataTest='marketplace-settings-dialog'
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
        },
        {
          label: 'Save',
          type: 'confirm',
          disabled: savingSettings,
          action: saveMarketplaceSettings,
          dataTest: 'save-marketplace-settings',
        },
      ]}>
      <div className='row no-gutter ph2'>
        <div className='col-xs-4'>
          <CurrencyInput
            placeholder='$'
            label='List Rate'
            id='rate'
            value={currentSettings.rate}
            onChange={changeRate}
            dataTest='rate'
          />
        </div>
        <div className='col-xs-12 text-caption-semibold mb4'>Rate on the Transfix app and DAT.</div>
        <div className='col-xs-6'>
          <Switch
            onChange={changeFlag}
            label='List on Transfix app'
            id='marketOnMarketplace'
            checked={currentSettings.marketOnMarketplace}
            dataTest='list-on-tfx-app'
          />
        </div>
        <div className='col-xs-12 text-caption-semibold mb4'>
          This shipment will appear on the Transfix mobile app and web app.
        </div>
        <div className='col-xs-6'>
          <Switch
            onChange={changeFlag}
            label='Post to DAT'
            id='marketOnLoadBoards'
            checked={currentSettings.marketOnLoadBoards}
            dataTest='post-to-dat'
          />
        </div>
        <div className='col-xs-12 text-caption-semibold mb4'>
          Automatically post this shipment to DAT and create additional posts.
        </div>
      </div>
    </FormModal>
  )
})
